import { Alert, Button, Input, Tabs } from 'antd';
import { ChangeEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { getPageURL, PageURL } from 'router/pageURL';
import { removeRoutesFromApplicationRoutes } from 'utils/localStorage';
import { v4 as uuidv4 } from 'uuid';
import { colors } from '../../variables';
import { BtlApplicationFormPageEnum } from '../BtlApplicationFormPage/types';
import { ApplicationsTable } from './ApplicationsTable';
import { BtlApplicationsTable } from './BtlApplicationsTable';
import { ApplicationFormPageEnum } from '../ApplicationFormPage/types';
import { useBrokerOnboarding } from 'queries';
import { NewSignupPageSectionEnum } from '../SignupPage/enums';

type tabType = 'residential' | 'btl';

export const ApplicationsTabbedListingPage = () => {
  const {
    data: brokerOnboarding,
    isFetched: isBrokerOnboardingFetched,
    isError: isBrokerOnboardingError,
  } = useBrokerOnboarding();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<tabType>('residential');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchBtlInputValue, setSearchBtlInputValue] = useState('');
  const newApplicationUrl = `${getPageURL(PageURL.APPLICATION_EDIT, { id: uuidv4() })}?section=${
    ApplicationFormPageEnum.CRITERIA
  }`;
  const newBtlApplicationUrl = `${getPageURL(PageURL.BTL_APPLICATION_CREATE)}?section=${
    BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY
  }`;

  const isResidentalActiveTab = activeTab === 'residential';

  const isFullSignupCompleted =
    !isBrokerOnboardingError &&
    isBrokerOnboardingFetched &&
    brokerOnboarding?.section === NewSignupPageSectionEnum.COMPLETED;

  const searchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value);
  };

  const searchBtlHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchBtlInputValue(event.target.value);
  };

  const newApplicationCreateHandler = () => {
    removeRoutesFromApplicationRoutes();
    navigate(newApplicationUrl);
  };

  const checkAffordabilityHandler = () => {
    window.open(PageURL.BTL_AFFORDABILITY, '_blank', 'noreferrer');
  };

  const newBtlApplicationCreateHandler = () => {
    removeRoutesFromApplicationRoutes();
    navigate(newBtlApplicationUrl);
  };

  const persistTab = (value: tabType) => {
    setActiveTab(value);
    localStorage.setItem('activeApplicationsTab', value);
  };

  useEffect(() => {
    const activeTab = localStorage.getItem('activeApplicationsTab');
    if (activeTab) setActiveTab(activeTab as tabType);
  }, []);

  return (
    <ApplicationsTabbedListingPageBody
      activeTab={activeTab}
      persistTab={persistTab}
      applicationCreateHandler={isResidentalActiveTab ? newApplicationCreateHandler : newBtlApplicationCreateHandler}
      checkAffordabilityHandler={checkAffordabilityHandler}
      searchHandler={isResidentalActiveTab ? searchHandler : searchBtlHandler}
      searchInputValue={isResidentalActiveTab ? searchInputValue : searchBtlInputValue}
      isFullSignupCompleted={isFullSignupCompleted}
    />
  );
};

type ApplicationsTabbedListingPageBodyProps = {
  isFullSignupCompleted: boolean;
  persistTab: (value: tabType) => void;
  activeTab: tabType;
  searchHandler: (event: ChangeEvent<HTMLInputElement>) => void;
  searchInputValue: string;
  applicationCreateHandler: () => void;
  checkAffordabilityHandler: () => void;
};

const ApplicationsTabbedListingPageBody = ({
  isFullSignupCompleted,
  persistTab,
  activeTab,
  searchInputValue,
  searchHandler,
  applicationCreateHandler,
  checkAffordabilityHandler,
}: ApplicationsTabbedListingPageBodyProps) => {
  const intl = useIntl();

  return (
    <div className={'w-full p-4'}>
      {!isFullSignupCompleted && (
        <Alert
          type={'warning'}
          showIcon
          className={'mb-4'}
          message={
            <>
              Your profile is incomplete. Please finish your registration by clicking{' '}
              <Link to={PageURL.SIGNUP} style={{ color: colors.primary }} className={'text-sm no-underline'}>
                here
              </Link>
            </>
          }
        ></Alert>
      )}
      <Tabs
        onChange={value => persistTab(value as tabType)}
        size={'large'}
        activeKey={activeTab}
        tabBarExtraContent={{
          right: (
            <div className={'flex gap-2'}>
              <Input
                size={'large'}
                onChange={searchHandler}
                value={searchInputValue}
                placeholder={'Search'}
                allowClear
              />
              <Button size={'large'} type={'primary'} onClick={applicationCreateHandler}>
                <FormattedMessage
                  id={activeTab === 'residential' ? 'cta.createNewApplication' : 'cta.createBtlNewApplication'}
                />
              </Button>
              {activeTab === 'btl' && (
                <Button size={'large'} type={'default'} onClick={checkAffordabilityHandler}>
                  <FormattedMessage id={'cta.checkAffordability'} />
                </Button>
              )}
            </div>
          ),
        }}
        items={[
          {
            label: intl.formatMessage({ id: 'application.header.residential' }),
            key: 'residential',
            children: <ApplicationsTable searchInputValue={searchInputValue} />,
          },
          {
            label: intl.formatMessage({ id: 'application.header.buyToLet' }),
            key: 'btl',
            children: <BtlApplicationsTable searchInputValue={searchInputValue} />,
          },
        ]}
      />
    </div>
  );
};
